import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SIGN_UP } from "../auth/AuthActions";

const USERNAME_RE = /^[A-Za-z0-9_]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUp() {
  const [form, setForm] = useState({
    username: "",
    email: "",
    password1: "",
    password2: "",
  });
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [errors, setErrors] = useState({});     // { field: "message" }
  const [touched, setTouched] = useState({});   // { field: true }
  const [topError, setTopError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // --- validation helpers ---
  const validateField = (k, v, state = form) => {
    switch (k) {
      case "username": {
        if (!v.trim()) return "Username is required.";
        if (v.length < 3) return "Username must be at least 3 characters.";
        if (v.length > 20) return "Username must be 20 characters or fewer.";
        if (!USERNAME_RE.test(v)) return "Only letters, numbers, and underscores.";
        return "";
      }
      case "email": {
        if (!v.trim()) return "Email is required.";
        if (!EMAIL_RE.test(v)) return "Enter a valid email.";
        return "";
      }
      case "password1": {
        if (!v) return "Password is required.";
        if (v.length < 6) return "Password must be at least 6 characters.";
        return "";
      }
      case "password2": {
        if (!v) return "Please confirm your password.";
        if (v !== state.password1) return "Passwords do not match.";
        return "";
      }
      default:
        return "";
    }
  };

  const validateAll = (state = form) => {
    const e = {};
    ["username", "email", "password1", "password2"].forEach((k) => {
      const msg = validateField(k, state[k], state);
      if (msg) e[k] = msg;
    });
    return e;
  };

  // --- handlers ---
  const handleChange = (e) => {
    const { id, value } = e.target;
    let newVal = value;

    if (id === "username") {
      // sanitize immediately so bad chars never stick
      newVal = value.replace(/[^A-Za-z0-9_]/g, "");
    }

    const next = { ...form, [id]: newVal };
    setForm(next);
    setTopError("");

    // live-validate this field
    const msg = validateField(id, newVal, next);

    setErrors((prev) => {
      const copy = { ...prev };
      if (msg) copy[id] = msg;
      else delete copy[id];

      // keep password2 in sync when password1 changes
      if (id === "password1" && next.password2) {
        const pw2Msg = validateField("password2", next.password2, next);
        if (pw2Msg) copy.password2 = pw2Msg;
        else delete copy.password2;
      }
      return copy;
    });
  };

  const handleBlur = (e) => {
    const { id } = e.target;
    setTouched((t) => ({ ...t, [id]: true }));
    const msg = validateField(id, form[id], form);
    setErrors((prev) => (msg ? { ...prev, [id]: msg } : (() => {
      const { [id]: _, ...rest } = prev; return rest;
    })()));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTopError("");

    const eAll = validateAll();
    setTouched({ username: true, email: true, password1: true, password2: true });

    if (Object.keys(eAll).length) {
      setErrors(eAll);
      setTopError("Please fix the errors below.");
      return;
    }

    try {
      setLoading(true);
      await SIGN_UP({
        username: form.username,
        email: form.email,
        password1: form.password1,
        password2: form.password2,
      });
      navigate("/accounts/signin");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Sign up failed. Try a different username/email.";
      setTopError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container signup-custom mt-3">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="signin-card">
            <h1 className="mb-4 text-center">Create your account</h1>

            {topError ? <div className="alert alert-danger py-2">{topError}</div> : null}

            <form onSubmit={handleSubmit} noValidate>
              {/* Username */}
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Username</label>
                <input
                  id="username"
                  type="text"
                  className={`form-control i7-input ${touched.username && errors.username ? "is-invalid" : ""}`}
                  value={form.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="your_handle"
                  autoComplete="username"
                  aria-describedby="usernameHelp"
                  aria-invalid={!!(touched.username && errors.username)}
                />
                <div id="usernameHelp" className="form-text">
                  Only letters, numbers, and underscores. 3–20 characters.
                </div>
                {touched.username && errors.username && (
                  <div className="invalid-feedback d-block">{errors.username}</div>
                )}
              </div>

              {/* Email */}
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  className={`form-control i7-input ${touched.email && errors.email ? "is-invalid" : ""}`}
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="you@example.com"
                  autoComplete="email"
                  aria-invalid={!!(touched.email && errors.email)}
                />
                {touched.email && errors.email && (
                  <div className="invalid-feedback d-block">{errors.email}</div>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label htmlFor="password1" className="form-label">Password</label>
                <div className="i7-input-wrap">
                  <input
                    id="password1"
                    type={showPw1 ? "text" : "password"}
                    className={`form-control i7-input ${touched.password1 && errors.password1 ? "is-invalid" : ""}`}
                    value={form.password1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    aria-describedby="passwordHelp"
                    aria-invalid={!!(touched.password1 && errors.password1)}
                  />
                  <button
                    type="button"
                    className="i7-toggle-btn"
                    onClick={() => setShowPw1((s) => !s)}
                    aria-label={showPw1 ? "Hide password" : "Show password"}
                  >
                    {showPw1 ? "Hide" : "Show"}
                  </button>
                </div>
                <div id="passwordHelp" className="form-text">
                  At least 6 characters.
                </div>
                {touched.password1 && errors.password1 && (
                  <div className="invalid-feedback d-block">{errors.password1}</div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-2">
                <label htmlFor="password2" className="form-label">Confirm Password</label>
                <div className="i7-input-wrap">
                  <input
                    id="password2"
                    type={showPw2 ? "text" : "password"}
                    className={`form-control i7-input ${touched.password2 && errors.password2 ? "is-invalid" : ""}`}
                    value={form.password2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    aria-invalid={!!(touched.password2 && errors.password2)}
                  />
                  <button
                    type="button"
                    className="i7-toggle-btn"
                    onClick={() => setShowPw2((s) => !s)}
                    aria-label={showPw2 ? "Hide password" : "Show password"}
                  >
                    {showPw2 ? "Hide" : "Show"}
                  </button>
                </div>
                {touched.password2 && errors.password2 && (
                  <div className="invalid-feedback d-block">{errors.password2}</div>
                )}
              </div>

              {/* CTA */}
              <button type="submit" className="btn i7-btn-teal w-100" disabled={loading}>
                <strong>{loading ? "Creating account…" : "Create account"}</strong>
              </button>
            </form>

            <div className="mt-3 text-center small">
              Already have an account?{" "}
              <Link to="/accounts/signin" className="i7-link-strong">Sign in</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
